import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { HelpArticle, HelpArticleType, HelpModule, HelpSubModule } from '../types/help';
import HelpContent from '../components/help/HelpContent';
import HelpLayout from '../components/help/HelpLayout';
import HelpSection from '../components/help/HelpSection';
import { HELP_DOC_THEME, HELP_DOC_TYPOGRAPHY } from '../components/help/helpTheme';
import { getHelpPathForPage } from '../utils/helpPageMapping';
import { DFT_BRAND, zoomedMainMinHeight } from '../styles/brandPalette';

type ViewState = 'welcome' | 'module' | 'submodule' | 'article' | 'search';
const CONTENT_MAX_WIDTH_CSS = '100%';
const HELP_CARD_RADIUS = '14px';
const helpPageSx = {
  width: '100%',
  maxWidth: 'none',
  minWidth: 0,
  alignSelf: 'stretch',
  flex: 1,
  px: 0,
  py: 0,
  bgcolor: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'hidden',
  boxSizing: 'border-box' as const,
  minHeight: {
    xs: 'calc(100dvh - 64px)',
    ...zoomedMainMinHeight(),
  },
  '& .MuiAlert-root': {
    borderRadius: '12px',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
  },
} as const;
const HELP_WORKSPACE_SX = {
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '0 12px 12px 12px',
  border: `1px solid ${DFT_BRAND.line}`,
  borderTop: `1px solid ${DFT_BRAND.line}`,
  bgcolor: DFT_BRAND.bg,
  boxShadow: 'none',
  overflow: 'hidden',
  p: 0,
} as const;
const helpArticleCardSx = {
  p: HELP_DOC_TYPOGRAPHY.articlePadding,
  borderRadius: `${HELP_CARD_RADIUS} !important`,
  border: `1px solid ${DFT_BRAND.line}`,
  bgcolor: DFT_BRAND.paper,
  boxShadow: '0 1px 2px rgba(23, 36, 65, 0.04)',
} as const;
const helpBtnSx = {
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: '0.875rem',
  height: 36,
  minHeight: 36,
  whiteSpace: 'nowrap' as const,
  boxShadow: 'none',
};
const helpDialogPaperSx = {
  width: { xs: 'calc(100% - 32px)', sm: 520 },
  maxWidth: 'min(92vw, 560px)',
  maxHeight: 'calc(100vh - 160px)',
  m: 2,
  borderRadius: '14px',
  border: `1px solid ${DFT_BRAND.line}`,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
} as const;

const Help: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [modules, setModules] = useState<HelpModule[]>([]);
  const [subModulesByModuleId, setSubModulesByModuleId] = useState<Record<string, HelpSubModule[]>>({});
  const [articlesBySubModuleId, setArticlesBySubModuleId] = useState<Record<string, HelpArticle[]>>({});
  const [expandedModuleIds, setExpandedModuleIds] = useState<Record<string, boolean>>({});
  const [expandedSubModuleIds, setExpandedSubModuleIds] = useState<Record<string, boolean>>({});
  const [loadingSubModuleModuleIds, setLoadingSubModuleModuleIds] = useState<Record<string, boolean>>({});
  const [loadingArticlesSubModuleIds, setLoadingArticlesSubModuleIds] = useState<Record<string, boolean>>({});
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);

  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<HelpArticle[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const loadedSubModulesRef = useRef<Set<string>>(new Set());
  const loadedArticlesRef = useRef<Set<string>>(new Set());

  const moduleId = searchParams.get('module') || '';
  const subModuleId = searchParams.get('subModule') || '';
  const articleId = searchParams.get('article') || '';
  const isMdUp = useMediaQuery('(min-width:900px)');

  const subModules = useMemo(
    () => (moduleId ? subModulesByModuleId[moduleId] ?? [] : []),
    [moduleId, subModulesByModuleId]
  );
  const articles = useMemo(
    () => (subModuleId ? articlesBySubModuleId[subModuleId] ?? [] : []),
    [subModuleId, articlesBySubModuleId]
  );
  const loadingSubModules = !!loadingSubModuleModuleIds[moduleId];
  const loadingArticles = !!loadingArticlesSubModuleIds[subModuleId];

  const currentModule = useMemo(
    () => modules.find((m) => m.id === moduleId) || null,
    [modules, moduleId]
  );
  const currentSubModule = useMemo(
    () => subModules.find((s) => s.id === subModuleId) || null,
    [subModules, subModuleId]
  );

  const updateParams = (next: { module?: string; subModule?: string; article?: string }) => {
    const params = new URLSearchParams(searchParams.toString());
    if (next.module !== undefined) next.module ? params.set('module', next.module) : params.delete('module');
    if (next.subModule !== undefined) next.subModule ? params.set('subModule', next.subModule) : params.delete('subModule');
    if (next.article !== undefined) next.article ? params.set('article', next.article) : params.delete('article');
    setSearchParams(params, { replace: false });
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingModules(true);
        setError(null);
        setModules(await apiService.getHelpModules());
      } catch {
        setError('Failed to load help modules.');
      } finally {
        setLoadingModules(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!moduleId) return;
    setExpandedModuleIds((e) => ({ ...e, [moduleId]: true }));
    if (loadedSubModulesRef.current.has(moduleId)) return;

    let cancelled = false;
    setLoadingSubModuleModuleIds((l) => ({ ...l, [moduleId]: true }));
    setError(null);
    const load = async () => {
      try {
        const data = await apiService.getHelpSubModules(moduleId);
        if (!cancelled) {
          loadedSubModulesRef.current.add(moduleId);
          setSubModulesByModuleId((prev) => ({ ...prev, [moduleId]: data }));
        }
      } catch {
        if (!cancelled) setError('Failed to load sub-modules.');
      } finally {
        if (!cancelled) {
          setLoadingSubModuleModuleIds((l) => {
            const n = { ...l };
            delete n[moduleId];
            return n;
          });
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [moduleId]);

  // Parent selected -> default child selected (auto).
  useEffect(() => {
    if (!moduleId || loadingSubModules || subModuleId || subModules.length === 0) return;
    const defaultSubModule = subModules.find((s) => s.defaultSubModule) || subModules[0];
    updateParams({ subModule: defaultSubModule.id, article: '' });
  }, [moduleId, loadingSubModules, subModuleId, subModules]);

  useEffect(() => {
    if (!subModuleId) return;
    setExpandedSubModuleIds((e) => ({ ...e, [subModuleId]: true }));
    if (loadedArticlesRef.current.has(subModuleId)) return;

    let cancelled = false;
    setLoadingArticlesSubModuleIds((l) => ({ ...l, [subModuleId]: true }));
    setError(null);
    const load = async () => {
      try {
        const data = await apiService.getHelpArticlesForSubModule(subModuleId);
        if (!cancelled) {
          loadedArticlesRef.current.add(subModuleId);
          setArticlesBySubModuleId((prev) => ({ ...prev, [subModuleId]: data }));
        }
      } catch {
        if (!cancelled) setError('Failed to load articles.');
      } finally {
        if (!cancelled) {
          setLoadingArticlesSubModuleIds((l) => {
            const n = { ...l };
            delete n[subModuleId];
            return n;
          });
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [subModuleId]);

  useEffect(() => {
    if (moduleId) setExpandedModuleIds((e) => ({ ...e, [moduleId]: true }));
    if (subModuleId) setExpandedSubModuleIds((e) => ({ ...e, [subModuleId]: true }));
  }, [moduleId, subModuleId]);

  useEffect(() => {
    if (!articleId) {
      setSelectedArticle(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoadingArticle(true);
        setError(null);
        const data = await apiService.getHelpArticle(articleId);
        if (!cancelled) setSelectedArticle(data);
      } catch {
        if (!cancelled) setError('Failed to load article.');
      } finally {
        if (!cancelled) setLoadingArticle(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [articleId]);

  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    const delay = setTimeout(async () => {
      try {
        setSearchLoading(true);
        // Keep search global so keywords return related articles across all modules.
        const data = await apiService.searchHelpArticles(q);
        if (!cancelled) setSearchResults(data);
      } catch {
        if (!cancelled) {
          setError('Failed to search help articles.');
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(delay);
    };
  }, [searchQuery, moduleId, subModuleId]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchModalOpen(true);
      }
      if (e.key === 'Escape') {
        setSearchModalOpen(false);
      }
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const t = e.target as HTMLElement;
        if (t.closest('input, textarea, [contenteditable=true]')) return;
        e.preventDefault();
        setSearchModalOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const fetchSubModulesIfNeeded = async (mid: string) => {
    if (loadedSubModulesRef.current.has(mid)) return;
    setLoadingSubModuleModuleIds((l) => ({ ...l, [mid]: true }));
    try {
      const data = await apiService.getHelpSubModules(mid);
      loadedSubModulesRef.current.add(mid);
      setSubModulesByModuleId((prev) => ({ ...prev, [mid]: data }));
    } catch {
      setError('Failed to load sub-modules.');
    } finally {
      setLoadingSubModuleModuleIds((l) => {
        const n = { ...l };
        delete n[mid];
        return n;
      });
    }
  };

  const fetchArticlesIfNeeded = async (sid: string): Promise<HelpArticle[]> => {
    if (loadedArticlesRef.current.has(sid)) return articlesBySubModuleId[sid] ?? [];
    setLoadingArticlesSubModuleIds((l) => ({ ...l, [sid]: true }));
    try {
      const data = await apiService.getHelpArticlesForSubModule(sid);
      loadedArticlesRef.current.add(sid);
      setArticlesBySubModuleId((prev) => ({ ...prev, [sid]: data }));
      return data;
    } catch {
      setError('Failed to load articles.');
      return [];
    } finally {
      setLoadingArticlesSubModuleIds((l) => {
        const n = { ...l };
        delete n[sid];
        return n;
      });
    }
  };

  const handleToggleModuleExpand = (mid: string) => {
    const willOpen = !expandedModuleIds[mid];
    setExpandedModuleIds((prev) => ({ ...prev, [mid]: !prev[mid] }));
    if (willOpen) void fetchSubModulesIfNeeded(mid);
  };

  const handleModuleNavigate = (mid: string) => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchModalOpen(false);
    setExpandedModuleIds((e) => ({ ...e, [mid]: true }));
    void fetchSubModulesIfNeeded(mid);
    updateParams({ module: mid, subModule: '', article: '' });
    setMobileNavOpen(false);
  };

  const handleToggleSubModuleExpand = (sid: string) => {
    const willOpen = !expandedSubModuleIds[sid];
    setExpandedSubModuleIds((prev) => ({ ...prev, [sid]: !prev[sid] }));
    if (willOpen) void fetchArticlesIfNeeded(sid);
  };

  const handleSubModuleNavigate = async (modId: string, subId: string) => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchModalOpen(false);
    setExpandedModuleIds((e) => ({ ...e, [modId]: true }));
    setExpandedSubModuleIds((e) => ({ ...e, [subId]: true }));
    await fetchSubModulesIfNeeded(modId);
    const availableArticles = await fetchArticlesIfNeeded(subId);
    const primaryArticleId = availableArticles[0]?.id;
    updateParams({ module: modId, subModule: subId, article: primaryArticleId || '' });
    setMobileNavOpen(false);
  };

  const handleArticleNavigate = (modId: string, subId: string, artId: string) => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchModalOpen(false);
    setExpandedModuleIds((e) => ({ ...e, [modId]: true }));
    setExpandedSubModuleIds((e) => ({ ...e, [subId]: true }));
    void fetchSubModulesIfNeeded(modId);
    void fetchArticlesIfNeeded(subId);
    updateParams({ module: modId, subModule: subId, article: artId });
    setMobileNavOpen(false);
  };

  const openArticleFromSearch = (article: HelpArticle) => {
    setSearchModalOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setExpandedModuleIds((e) => ({ ...e, [article.moduleId]: true }));
    setExpandedSubModuleIds((e) => ({ ...e, [article.subModuleId]: true }));
    void fetchSubModulesIfNeeded(article.moduleId);
    void fetchArticlesIfNeeded(article.subModuleId);
    updateParams({ module: article.moduleId, subModule: article.subModuleId, article: article.id });
  };

  const toReadableLabel = (value?: string) =>
    (value || '')
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const getSearchMeta = (article: HelpArticle) => {
    const moduleLabel = toReadableLabel(article.moduleId);
    const subModuleLabel = toReadableLabel(article.subModuleId);
    return `${renderType(article.type)} • ${moduleLabel}${subModuleLabel ? ` / ${subModuleLabel}` : ''}`;
  };

  const renderType = (type: HelpArticleType) => {
    if (type === 'OVERVIEW') return 'Overview';
    if (type === 'HOW_TO') return 'How-to';
    if (type === 'TROUBLESHOOTING') return 'Troubleshooting';
    if (type === 'CONCEPT') return 'Concept';
    return type;
  };

  const groupedByType = useMemo(() => {
    const groups: Record<HelpArticleType, HelpArticle[]> = {
      OVERVIEW: [],
      HOW_TO: [],
      TROUBLESHOOTING: [],
      CONCEPT: [],
    };
    articles.forEach((a) => groups[a.type]?.push(a));
    return groups;
  }, [articles]);

  const viewState: ViewState = useMemo(() => {
    if (searchQuery.trim() && searchResults.length > 0) return 'search';
    if (!moduleId) return 'welcome';
    if (moduleId && !subModuleId) return 'module';
    if (moduleId && subModuleId && !articleId) return 'submodule';
    return 'article';
  }, [moduleId, subModuleId, articleId, searchQuery, searchResults.length]);

  const breadcrumbs = useMemo(() => {
    const items: { label: string; onClick?: () => void }[] = [
      { label: 'Help Center', onClick: () => updateParams({ module: '', subModule: '', article: '' }) },
    ];
    if (currentModule) items.push({ label: currentModule.name, onClick: () => updateParams({ subModule: '', article: '' }) });
    if (currentSubModule) items.push({ label: currentSubModule.name, onClick: () => updateParams({ article: '' }) });
    if (selectedArticle) items.push({ label: selectedArticle.title });
    return items;
  }, [currentModule, currentSubModule, selectedArticle]);

  return (
    <Box sx={helpPageSx}>
      <Box sx={{ width: '100%', flexShrink: 0, overflow: 'visible' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: 1,
            minWidth: 0,
            px: { xs: 1.25, sm: 1.5 },
            pt: 1.25,
            pb: 0.5,
            minHeight: 36,
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              m: 0,
              fontWeight: 700,
              height: 36,
              lineHeight: '36px',
              letterSpacing: '-0.02em',
              color: DFT_BRAND.text,
              whiteSpace: 'nowrap',
            }}
          >
            Help
          </Typography>
        </Box>
      </Box>

      <Box sx={HELP_WORKSPACE_SX}>
    <HelpLayout
      modules={modules}
      subModulesByModuleId={subModulesByModuleId}
      articlesBySubModuleId={articlesBySubModuleId}
      expandedModuleIds={expandedModuleIds}
      expandedSubModuleIds={expandedSubModuleIds}
      loadingSubModuleModuleIds={loadingSubModuleModuleIds}
      loadingArticlesSubModuleIds={loadingArticlesSubModuleIds}
      selectedModuleId={moduleId}
      selectedSubModuleId={subModuleId}
      selectedArticleId={articleId}
      loadingModules={loadingModules}
      onModuleNavigate={handleModuleNavigate}
      onToggleModuleExpand={handleToggleModuleExpand}
      onToggleSubModuleExpand={handleToggleSubModuleExpand}
      onSubModuleNavigate={handleSubModuleNavigate}
      onArticleNavigate={handleArticleNavigate}
      onOpenSearch={() => setSearchModalOpen(true)}
      sidebarSearchQuery={searchQuery}
      onSidebarSearchChange={setSearchQuery}
      mobileNavOpen={mobileNavOpen}
      onCloseMobileNav={() => setMobileNavOpen(false)}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 36,
          flexShrink: 0,
          px: { xs: 1, sm: 1.25 },
          py: 0.5,
          borderBottom: `1px solid ${DFT_BRAND.line}`,
          bgcolor: DFT_BRAND.paper,
          minWidth: 0,
        }}
      >
          {!isMdUp && (
            <IconButton
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open help navigation"
              sx={{ width: 32, height: 32, flexShrink: 0 }}
            >
              <MenuIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}

          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Breadcrumbs sx={{ maxWidth: '100%', overflow: 'hidden', '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' } }}>
              {breadcrumbs.map((b, i) => (
                b.onClick && i !== breadcrumbs.length - 1 ? (
                  <Button
                    key={`${b.label}-${i}`}
                    onClick={b.onClick}
                    size="small"
                    variant="text"
                    sx={{
                      textTransform: 'none',
                      px: 0.5,
                      minWidth: 0,
                      maxWidth: { xs: 100, sm: 140, md: 180 },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: '#1d4ed8',
                      fontSize: '0.8125rem',
                      height: 32,
                      '&:hover': { bgcolor: HELP_DOC_THEME.accentSoft },
                    }}
                  >
                    {b.label}
                  </Button>
                ) : (
                  <Typography
                    key={`${b.label}-${i}`}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      maxWidth: { xs: 120, sm: 180, md: 220 },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: DFT_BRAND.text,
                    }}
                  >
                    {b.label}
                  </Typography>
                )
              ))}
            </Breadcrumbs>
          </Box>

          <Button
            size="small"
            variant="outlined"
            onClick={() => setSearchModalOpen(true)}
            sx={{
              ...helpBtnSx,
              flexShrink: 0,
              color: '#1d4ed8',
              borderColor: '#bfdbfe',
              bgcolor: '#ffffff',
              px: 1.1,
              '&:hover': { borderColor: '#93c5fd', bgcolor: '#eff6ff' },
            }}
          >
            Search
          </Button>

          <IconButton
            size="small"
            aria-label="Open help in new tab"
            onClick={() => window.open(`/help?${searchParams.toString()}`, '_blank', 'noopener,noreferrer')}
            sx={{ flexShrink: 0, width: 32, height: 32 }}
          >
            <OpenInNewIcon sx={{ fontSize: 18 }} />
          </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: { xs: 1, md: 2 }, py: { xs: 1, md: 1.5 } }}>
        <Box sx={{ maxWidth: CONTENT_MAX_WIDTH_CSS, mx: 'auto' }}>
          {error && (
            <Paper elevation={0} sx={{ mb: 1.5, p: 1.5, borderRadius: '14px', border: '1px solid', borderColor: 'error.main', bgcolor: 'rgba(211,47,47,0.04)' }}>
              <Typography color="error" variant="body2" sx={{ fontWeight: 600 }}>
                {error}
              </Typography>
            </Paper>
          )}

          {viewState === 'welcome' && (
            <Paper
              elevation={0}
              sx={{
                ...helpArticleCardSx,
                borderTop: `3px solid ${HELP_DOC_THEME.accent}`,
              }}
            >
              <Typography component="h1" sx={{ ...HELP_DOC_TYPOGRAPHY.h1, mt: 0 }}>
                Welcome to the docs
              </Typography>
              <Typography component="p" sx={{ ...HELP_DOC_TYPOGRAPHY.paragraph, mt: 0 }}>
                Start with <strong>Introduction &amp; reference</strong> for a concise map of product features and functions
                (including Getting started, Projects, Collections &amp; Requests, Dashboard, and related areas). Then explore the
                sidebar or use search (Ctrl/Cmd+K) to open any guide.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: '15px', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() =>
                    navigate(
                      '/help?module=help-foundations&subModule=help-foundations-intro&article=help-foundations-intro-article'
                    )
                  }
                  sx={{
                    ...helpBtnSx,
                    bgcolor: '#1d4ed8',
                    '&:hover': { bgcolor: '#1e40af' },
                  }}
                >
                  Introduction &amp; features
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(getHelpPathForPage('DASHBOARD'))}
                  sx={{ ...helpBtnSx, borderColor: DFT_BRAND.line, color: HELP_DOC_THEME.link }}
                >
                  Getting started
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(getHelpPathForPage('AI_AGENT'))}
                  sx={{ ...helpBtnSx, borderColor: '#1d4ed8', color: '#1e40af' }}
                >
                  AI features
                </Button>
              </Stack>
            </Paper>
          )}

          {viewState === 'module' && currentModule && (
            <Paper elevation={0} sx={helpArticleCardSx}>
              <Typography component="h1" sx={{ ...HELP_DOC_TYPOGRAPHY.h1, mt: 0 }}>
                {currentModule.name}
              </Typography>
              {currentModule.description && (
                <Typography component="p" sx={{ ...HELP_DOC_TYPOGRAPHY.paragraph, mt: 0 }}>
                  {currentModule.description}
                </Typography>
              )}
            </Paper>
          )}

          {viewState === 'submodule' && currentSubModule && (
            <Stack spacing={2}>
              <Paper
                variant="outlined"
                sx={helpArticleCardSx}
              >
                <Typography component="h1" sx={{ ...HELP_DOC_TYPOGRAPHY.h1, mt: 0 }}>
                  {currentSubModule.name}
                </Typography>
                {currentSubModule.description && (
                  <Typography component="p" sx={{ ...HELP_DOC_TYPOGRAPHY.paragraph, mt: 0 }}>
                    {currentSubModule.description}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
                  <Chip label="What it does" variant="outlined" />
                  <Chip label="Step-by-step" variant="outlined" />
                  <Chip label="Best practices" variant="outlined" />
                </Stack>
              </Paper>

              <Paper
                variant="outlined"
                sx={helpArticleCardSx}
              >
                {loadingArticles ? (
                  <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={22} />
                  </Box>
                ) : articles.length === 0 ? (
                  <Typography component="p" sx={HELP_DOC_TYPOGRAPHY.paragraph}>
                    No articles available yet.
                  </Typography>
                ) : (
                  <Stack>
                    {Object.entries(groupedByType).map(([typeKey, list]) => {
                      const typed = typeKey as HelpArticleType;
                      if (!list.length) return null;
                      return (
                        <HelpSection
                          key={typeKey}
                          id={`help-type-${typeKey}`}
                          title={renderType(typed)}
                          subtitle={`${list.length} ${list.length === 1 ? 'article' : 'articles'}`}
                        >
                          <List dense disablePadding>
                            {list.map((article) => (
                              <ListItemButton
                                key={article.id}
                                onClick={() => updateParams({ article: article.id })}
                                sx={{ border: 1, borderColor: 'divider', borderRadius: 1.25, mb: 0.75 }}
                              >
                                <ListItemText
                                  primary={article.title}
                                  secondary={article.summary || undefined}
                                  primaryTypographyProps={{
                                    fontWeight: 700,
                                    fontSize: '0.875rem',
                                    lineHeight: HELP_DOC_TYPOGRAPHY.lineHeight,
                                    fontFamily: HELP_DOC_THEME.fontBody,
                                  }}
                                  secondaryTypographyProps={{
                                    noWrap: true,
                                    sx: { fontSize: '14px', lineHeight: 1.45, mt: '4px' },
                                  }}
                                />
                              </ListItemButton>
                            ))}
                          </List>
                        </HelpSection>
                      );
                    })}
                  </Stack>
                )}
              </Paper>
            </Stack>
          )}

          {viewState === 'article' && selectedArticle && (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <Chip size="small" label={renderType(selectedArticle.type)} variant="outlined" />
                {currentModule?.name && <Chip size="small" label={currentModule.name} variant="outlined" />}
                {currentSubModule?.name && <Chip size="small" label={currentSubModule.name} variant="outlined" />}
              </Stack>
              {loadingArticle ? (
                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={22} />
                </Box>
              ) : (
                <HelpContent title={selectedArticle.title} summary={selectedArticle.summary} body={selectedArticle.body} />
              )}
            </Stack>
          )}

          {viewState === 'search' && (
            <Paper elevation={0} sx={helpArticleCardSx}>
              <Typography
                component="h2"
                sx={{
                  ...HELP_DOC_TYPOGRAPHY.h2,
                  mt: 0,
                  mb: HELP_DOC_TYPOGRAPHY.h2.marginBottom,
                }}
              >
                Search results
              </Typography>
              {!!searchQuery.trim() && !searchLoading && (
                <Typography component="p" sx={{ ...HELP_DOC_TYPOGRAPHY.paragraph, mt: 0 }}>
                  {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'} for "{searchQuery.trim()}"
                </Typography>
              )}
              {searchLoading ? (
                <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={22} />
                </Box>
              ) : searchResults.length === 0 ? (
                <Typography component="p" sx={HELP_DOC_TYPOGRAPHY.paragraph}>
                  No results found.
                </Typography>
              ) : (
                <List dense>
                  {searchResults.map((article) => (
                    <ListItemButton
                      key={article.id}
                      onClick={() => openArticleFromSearch(article)}
                      sx={{ border: 1, borderColor: 'divider', borderRadius: 1.25, mb: 0.75 }}
                    >
                      <ListItemText
                        primary={article.title}
                        secondary={article.summary || getSearchMeta(article)}
                        primaryTypographyProps={{
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          lineHeight: HELP_DOC_TYPOGRAPHY.lineHeight,
                          fontFamily: HELP_DOC_THEME.fontBody,
                        }}
                        secondaryTypographyProps={{
                          sx: { fontSize: '14px', lineHeight: 1.45, mt: '4px' },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Paper>
          )}
        </Box>
      </Box>

      <Dialog
        open={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        maxWidth={false}
        aria-labelledby="help-search-dialog-title"
        PaperProps={{ sx: helpDialogPaperSx }}
      >
        <DialogTitle
          id="help-search-dialog-title"
          sx={{
            pb: 1.25,
            pt: 1.5,
            fontWeight: 700,
            fontSize: '1.05rem',
            color: DFT_BRAND.text,
          }}
        >
          Search Documentation
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5, fontFamily: HELP_DOC_THEME.fontBody, fontSize: '0.875rem' }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search topics, features, setup guides..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchResults.length > 0) {
                e.preventDefault();
                openArticleFromSearch(searchResults[0]);
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': { height: 40, fontSize: '0.875rem' },
            }}
          />
          <Typography
            component="p"
            sx={{ ...HELP_DOC_TYPOGRAPHY.paragraph, fontSize: '14px', color: HELP_DOC_THEME.textMuted, mt: 0 }}
          >
            Tip: Use Ctrl/Cmd + K to open search. Press Enter to open the first result.
          </Typography>
          <Paper
            elevation={0}
            sx={{
              maxHeight: 360,
              overflow: 'auto',
              borderRadius: '10px',
              border: `1px solid ${DFT_BRAND.line}`,
            }}
          >
            {searchLoading ? (
              <Box sx={{ py: 2.5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            ) : searchQuery.trim() === '' ? (
              <Typography component="p" sx={{ ...HELP_DOC_TYPOGRAPHY.paragraph, p: '16px', m: 0, color: HELP_DOC_THEME.textMuted }}>
                Start typing to find help topics.
              </Typography>
            ) : searchResults.length === 0 ? (
              <Typography component="p" sx={{ ...HELP_DOC_TYPOGRAPHY.paragraph, p: '16px', m: 0, color: HELP_DOC_THEME.textMuted }}>
                No results found for this keyword.
              </Typography>
            ) : (
              <List dense sx={{ py: 1 }}>
                {searchResults.map((article) => (
                  <ListItemButton
                    key={`modal-${article.id}`}
                    onClick={() => openArticleFromSearch(article)}
                    sx={{ mx: 1, mb: 0.5, borderRadius: 0 }}
                  >
                    <ListItemText
                      primary={article.title}
                      secondary={article.summary || getSearchMeta(article)}
                      primaryTypographyProps={{
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        lineHeight: HELP_DOC_TYPOGRAPHY.lineHeight,
                        fontFamily: HELP_DOC_THEME.fontBody,
                      }}
                      secondaryTypographyProps={{
                        sx: { fontSize: '14px', lineHeight: 1.45, mt: '4px' },
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </DialogContent>
      </Dialog>
    </HelpLayout>
      </Box>
    </Box>
  );
};

export default Help;
